import type {
  BackgroundBloomMode,
  BackgroundGlowMode,
  BackgroundMixMode,
  BackgroundMotionMode,
  BackgroundSaturationMode,
  BackgroundStyleMode,
  FpsCap,
  OpponentRenderMode,
  ParticleMode,
  PerformancePreset,
  PerformanceSettings,
  SideFxDensityMode,
  SideFxMode
} from "./types";
import type { DebugDiagnosticsDetail, DebugVerbosity } from "./debugSettings";

export const FPS_CAP_OPTIONS: readonly FpsCap[] = [60, 45, 30];
export const SIDE_FX_OPTIONS: readonly SideFxMode[] = ["FULL", "SUBTLE", "OFF"];
export const SIDE_FX_DENSITY_OPTIONS: readonly SideFxDensityMode[] = ["LOW", "MEDIUM", "HIGH"];
export const BG_STYLE_OPTIONS: readonly BackgroundStyleMode[] = ["SYNTHWAVE", "CYBER_GRID", "TUNNEL", "SEQUENCE"];
export const BG_BLOOM_OPTIONS: readonly BackgroundBloomMode[] = ["OFF", "LOW", "MEDIUM", "HIGH"];
export const BG_GLOW_OPTIONS: readonly BackgroundGlowMode[] = ["LOW", "MEDIUM", "HIGH"];
export const BG_SATURATION_OPTIONS: readonly BackgroundSaturationMode[] = ["LOW", "MEDIUM", "HIGH", "MAX"];
export const BG_MIX_OPTIONS: readonly BackgroundMixMode[] = ["LINES", "EVEN", "PARTICLES"];
export const BG_MOTION_OPTIONS: readonly BackgroundMotionMode[] = ["OFF", "CALM", "NORMAL", "FAST"];
export const PARTICLE_OPTIONS: readonly ParticleMode[] = ["FULL", "REDUCED", "OFF"];
export const OPPONENT_RENDER_OPTIONS: readonly OpponentRenderMode[] = ["ANIMATED", "SIMPLIFIED", "STATIC"];
export const DEBUG_VERBOSITY_OPTIONS: readonly DebugVerbosity[] = ["STANDARD", "VERBOSE"];
export const DEBUG_DIAGNOSTICS_OPTIONS: readonly DebugDiagnosticsDetail[] = ["OFF", "CORE", "FULL"];

const PRESET_VALUES: Record<Exclude<PerformancePreset, "CUSTOM">, Omit<PerformanceSettings, "preset">> = {
  QUALITY: {
    sideFxMode: "FULL",
    sideFxDensityMode: "HIGH",
    backgroundStyleMode: "SYNTHWAVE",
    backgroundBloomMode: "MEDIUM",
    backgroundGlowMode: "HIGH",
    backgroundSaturationMode: "MAX",
    backgroundMixMode: "EVEN",
    backgroundMotionMode: "FAST",
    particleMode: "FULL",
    opponentRenderMode: "ANIMATED",
    fpsCap: 60
  },
  BALANCED: {
    sideFxMode: "FULL",
    sideFxDensityMode: "HIGH",
    backgroundStyleMode: "SYNTHWAVE",
    backgroundBloomMode: "MEDIUM",
    backgroundGlowMode: "HIGH",
    backgroundSaturationMode: "MAX",
    backgroundMixMode: "EVEN",
    backgroundMotionMode: "NORMAL",
    particleMode: "REDUCED",
    opponentRenderMode: "ANIMATED",
    fpsCap: 60
  },
  PERFORMANCE: {
    sideFxMode: "OFF",
    sideFxDensityMode: "LOW",
    backgroundStyleMode: "CYBER_GRID",
    backgroundBloomMode: "OFF",
    backgroundGlowMode: "LOW",
    backgroundSaturationMode: "MEDIUM",
    backgroundMixMode: "LINES",
    backgroundMotionMode: "CALM",
    particleMode: "OFF",
    opponentRenderMode: "SIMPLIFIED",
    fpsCap: 45
  }
};

export const PERFORMANCE_PRESET_ORDER: ReadonlyArray<Exclude<PerformancePreset, "CUSTOM">> = [
  "QUALITY",
  "BALANCED",
  "PERFORMANCE"
];

export const DEFAULT_PERFORMANCE_SETTINGS: PerformanceSettings = {
  preset: "BALANCED",
  ...PRESET_VALUES.BALANCED
};

function includesValue<T extends string | number>(values: readonly T[], value: unknown): value is T {
  return values.includes(value as T);
}

export function cycleOption<T>(options: readonly T[], current: T): T {
  const index = options.indexOf(current);
  if (index < 0) {
    return options[0] as T;
  }
  return options[(index + 1) % options.length] as T;
}

export function clonePerformanceSettings(settings: PerformanceSettings): PerformanceSettings {
  return { ...settings };
}

export function applyPerformancePreset(preset: Exclude<PerformancePreset, "CUSTOM">): PerformanceSettings {
  return {
    preset,
    ...PRESET_VALUES[preset]
  };
}

export function resolvePerformancePreset(settings: Omit<PerformanceSettings, "preset">): PerformancePreset {
  for (const preset of PERFORMANCE_PRESET_ORDER) {
    const profile = PRESET_VALUES[preset];
    if (
      settings.sideFxMode === profile.sideFxMode
      && settings.sideFxDensityMode === profile.sideFxDensityMode
      && settings.backgroundStyleMode === profile.backgroundStyleMode
      && settings.backgroundBloomMode === profile.backgroundBloomMode
      && settings.backgroundGlowMode === profile.backgroundGlowMode
      && settings.backgroundSaturationMode === profile.backgroundSaturationMode
      && settings.backgroundMixMode === profile.backgroundMixMode
      && settings.backgroundMotionMode === profile.backgroundMotionMode
      && settings.particleMode === profile.particleMode
      && settings.opponentRenderMode === profile.opponentRenderMode
      && settings.fpsCap === profile.fpsCap
    ) {
      return preset;
    }
  }
  return "CUSTOM";
}

export function normalizePerformanceSettings(
  value: Partial<PerformanceSettings> | null | undefined
): PerformanceSettings {
  const rawSideFxMode = value?.sideFxMode;
  const rawSideFxDensityMode = value?.sideFxDensityMode;
  const rawBackgroundStyleMode = value?.backgroundStyleMode;
  const rawBackgroundBloomMode = value?.backgroundBloomMode;
  const rawBackgroundGlowMode = value?.backgroundGlowMode;
  const rawBackgroundSaturationMode = value?.backgroundSaturationMode;
  const rawBackgroundMixMode = value?.backgroundMixMode;
  const rawBackgroundMotionMode = value?.backgroundMotionMode;
  const rawParticleMode = value?.particleMode;
  const rawOpponentRenderMode = value?.opponentRenderMode;
  const rawFpsCap = value?.fpsCap;
  const rawPreset = value?.preset;

  const sideFxMode: SideFxMode = includesValue(SIDE_FX_OPTIONS, rawSideFxMode)
    ? rawSideFxMode
    : DEFAULT_PERFORMANCE_SETTINGS.sideFxMode;
  const sideFxDensityMode: SideFxDensityMode = includesValue(SIDE_FX_DENSITY_OPTIONS, rawSideFxDensityMode)
    ? rawSideFxDensityMode
    : DEFAULT_PERFORMANCE_SETTINGS.sideFxDensityMode;
  const backgroundStyleMode: BackgroundStyleMode = includesValue(BG_STYLE_OPTIONS, rawBackgroundStyleMode)
    ? rawBackgroundStyleMode
    : DEFAULT_PERFORMANCE_SETTINGS.backgroundStyleMode;
  const backgroundBloomMode: BackgroundBloomMode = includesValue(BG_BLOOM_OPTIONS, rawBackgroundBloomMode)
    ? rawBackgroundBloomMode
    : DEFAULT_PERFORMANCE_SETTINGS.backgroundBloomMode;
  const backgroundGlowMode: BackgroundGlowMode = includesValue(BG_GLOW_OPTIONS, rawBackgroundGlowMode)
    ? rawBackgroundGlowMode
    : DEFAULT_PERFORMANCE_SETTINGS.backgroundGlowMode;
  const backgroundSaturationMode: BackgroundSaturationMode = includesValue(BG_SATURATION_OPTIONS, rawBackgroundSaturationMode)
    ? rawBackgroundSaturationMode
    : DEFAULT_PERFORMANCE_SETTINGS.backgroundSaturationMode;
  const backgroundMixMode: BackgroundMixMode = includesValue(BG_MIX_OPTIONS, rawBackgroundMixMode)
    ? rawBackgroundMixMode
    : DEFAULT_PERFORMANCE_SETTINGS.backgroundMixMode;
  const backgroundMotionMode: BackgroundMotionMode = includesValue(BG_MOTION_OPTIONS, rawBackgroundMotionMode)
    ? rawBackgroundMotionMode
    : DEFAULT_PERFORMANCE_SETTINGS.backgroundMotionMode;
  const particleMode: ParticleMode = includesValue(PARTICLE_OPTIONS, rawParticleMode)
    ? rawParticleMode
    : DEFAULT_PERFORMANCE_SETTINGS.particleMode;
  const opponentRenderMode: OpponentRenderMode = includesValue(OPPONENT_RENDER_OPTIONS, rawOpponentRenderMode)
    ? rawOpponentRenderMode
    : DEFAULT_PERFORMANCE_SETTINGS.opponentRenderMode;
  const fpsCap: FpsCap = includesValue(FPS_CAP_OPTIONS, rawFpsCap)
    ? rawFpsCap
    : DEFAULT_PERFORMANCE_SETTINGS.fpsCap;

  const normalizedBase = {
    sideFxMode,
    sideFxDensityMode,
    backgroundStyleMode,
    backgroundBloomMode,
    backgroundGlowMode,
    backgroundSaturationMode,
    backgroundMixMode,
    backgroundMotionMode,
    particleMode,
    opponentRenderMode,
    fpsCap
  };
  const derivedPreset = resolvePerformancePreset(normalizedBase);

  if (
    rawPreset === "QUALITY"
    || rawPreset === "BALANCED"
    || rawPreset === "PERFORMANCE"
  ) {
    return applyPerformancePreset(rawPreset);
  }

  if (rawPreset === "CUSTOM") {
    return {
      preset: "CUSTOM",
      ...normalizedBase
    };
  }

  return {
    preset: derivedPreset,
    ...normalizedBase
  };
}
